import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";
import { APP_HOME } from "../routes/paths";
import axios from "axios";
import { toast } from "react-hot-toast";
import { LuPanelLeft, LuPanelRight, LuShare2 } from "react-icons/lu";
import { Video } from "../components/VideoTile";
import CommentsSection from "../components/QuickReview/CommentsSection";
import { AddCommentHandle } from "../components/QuickReview/AddComment";
import Transcript, { Word, fetchSubtitles } from "../components/QuickReview/Transcript";
import ResizablePanels from "../components/ResizeablePanel";
import { Comment, CommentResponse, VideoAnnotation } from "../components/QuickReview/types";
import ShareDialog from "../components/QuickReview/ShareDialog";

import VideoPlayer, { VideoPlayerHandle } from "../components/VideoPlayer";

import logEvent from "../amplitude";

interface QuickReviewProps {
    videoId?: string;
    isSharedAccess?: boolean;
    permission?: "viewer" | "commenter";
    shareToken?: string;
}

// How far, in seconds, the video can be from a drawing's frame and still count as on it.
const DRAWING_FRAME_TOLERANCE = 0.25;

const QuickReview: React.FC<QuickReviewProps> = ({
    videoId: propVideoId,
    isSharedAccess = false,
    permission = "viewer",
    shareToken,
}) => {
    const navigate = useNavigate();
    const { videoId: paramVideoId } = useParams<{ videoId: string }>();
    const actualVideoId = propVideoId || paramVideoId;

    const [video, setVideo] = useState<Video | null>(null);
    const [indexId, setIndexId] = useState<string>("");
    const [comments, setComments] = useState<Comment[]>([]);
    const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
    const [isLoadingComments, setIsLoadingComments] = useState<boolean>(false);
    const [words, setWords] = useState<Word[]>([]);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [streamUrl, setStreamUrl] = useState<string>("");
    const [isPlaying, setIsPlaying] = useState<boolean>(true);
    const [currentUserEmail, setCurrentUserEmail] = useState<string>("");
    const [showTranscript, setShowTranscript] = useState<boolean>(true);
    const [showComments, setShowComments] = useState<boolean>(true);
    const [canComment, setCanComment] = useState<boolean>(true);
    const [panelSizes, setPanelSizes] = useState<number[]>([0.2, 0.5, 0.3]);
    const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [draftAnnotation, setDraftAnnotation] = useState<VideoAnnotation | null>(null);
    const [annotationTime, setAnnotationTime] = useState<number | null>(null);
    const [isDrawing, setIsDrawing] = useState<boolean>(false);
    const [isSendingDrawing, setIsSendingDrawing] = useState<boolean>(false);
    const playerRef = useRef<VideoPlayerHandle>(null);
    const composerRef = useRef<AddCommentHandle>(null);
    const discardToastIdRef = useRef<string | null>(null);

    useEffect(() => {
        logEvent("page_view", {
            page_title: "Quick Review",
            page_location: window.location.href,
            page_path: window.location.pathname,
        });
    }, []);

    useEffect(() => {
        const savedSizes = localStorage.getItem("quick-review-panel-sizes");
        if (savedSizes) {
            const parsed = JSON.parse(savedSizes);
            if (Array.isArray(parsed) && parsed.length === 3 && parsed.every(s => s > 0)) {
                setPanelSizes(parsed);
            }
        }
    }, []);

    useEffect(() => {
        if (isSharedAccess) {
            setCanComment(permission === "commenter");
        }
    }, [isSharedAccess, permission]);

    const updatePanelSizes = useCallback((showingTranscript: boolean, showingComments: boolean) => {
        if (!showingTranscript && !showingComments) {
            setPanelSizes([0, 1, 0]);
        } else if (!showingTranscript && showingComments) {
            setPanelSizes([0, 0.6, 0.4]);
        } else if (showingTranscript && !showingComments) {
            setPanelSizes([0.4, 0.6, 0]);
        } else {
            setPanelSizes([0.3, 0.4, 0.3]);
        }
    }, []);

    const handleTranscriptToggle = useCallback(() => {
        setShowTranscript(prev => {
            const newShowTranscript = !prev;
            updatePanelSizes(newShowTranscript, showComments);
            return newShowTranscript;
        });
    }, [showComments, updatePanelSizes]);

    const handleCommentsToggle = useCallback(() => {
        setShowComments(prev => {
            const newShowComments = !prev;
            updatePanelSizes(showTranscript, newShowComments);
            return newShowComments;
        });
    }, [showTranscript, updatePanelSizes]);

    const fetchComments = useCallback(async (): Promise<void> => {
        try {
            setIsLoadingComments(true);
            const headers: Record<string, string> = {};

            if (isSharedAccess && shareToken) {
                headers["X-Share-Token"] = shareToken;
            } else {
                const token = localStorage.getItem("token");
                if (token) headers["Authorization"] = `Bearer ${token}`;
            }

            const response = await axios.get<CommentResponse>(
                `${process.env.REACT_APP_API}/api/quick-review/comments/${actualVideoId}`,
                { headers, params: { page: 1, page_size: 50 } }
            );
            setComments(response.data.items);
            setIndexId(response.data.index_id);
        } catch (error) {
            console.error("Error fetching comments:", error);
        } finally {
            setIsLoadingComments(false);
        }
    }, [actualVideoId, isSharedAccess, shareToken]);

    const handleProgress = useCallback(({ playedSeconds }: { playedSeconds: number }) => {
        setCurrentTime(playedSeconds);
    }, []);

    const handleCommentSelect = useCallback(
        (comment: Comment) => {
            setIsDrawing(false);
            if (selectedCommentId === comment.id && !isPlaying) {
                setIsPlaying(true);
                return;
            }

            setIsPlaying(false);
            playerRef.current?.seekTo(comment.start_time, "seconds");
            setCurrentTime(comment.start_time);
            setSelectedCommentId(comment.id);
        },
        [selectedCommentId, isPlaying]
    );

    useEffect(() => {
        const init = async () => {
            try {
                setIsLoading(true);
                setError(null);

                if (isSharedAccess) {
                    if (!shareToken) {
                        throw new Error("Invalid share token");
                    }

                    const response = await axios.get(
                        `${process.env.REACT_APP_API}/api/sharing_router/get_shared_video_details/${actualVideoId}`,
                        {
                            headers: {
                                "X-Share-Token": shareToken,
                            },
                        }
                    );

                    setVideo(response.data);
                    setStreamUrl(response.data.stream_url);
                    setCanComment(response.data.shared_permission === "commenter");

                    if (response.data.subtitle_word || response.data.subtitle_url) {
                        const subtitleUrl =
                            response.data.subtitle_word || response.data.subtitle_url;
                        const parsedWords = await fetchSubtitles(subtitleUrl);
                        setWords(parsedWords);
                    }
                } else {
                    const token = localStorage.getItem("token");
                    if (!token) {
                        navigate("/login");
                        return;
                    }

                    const headers = { Authorization: `Bearer ${token}` };

                    const [userResponse, videoResponse] = await Promise.all([
                        axios.get(`${process.env.REACT_APP_API}/api/get_user_details`, { headers }),
                        axios.get(
                            `${process.env.REACT_APP_API}/api/get_video_details/${actualVideoId}`,
                            { headers }
                        ),
                    ]);

                    setCurrentUserEmail(userResponse.data.email);
                    setVideo(videoResponse.data);
                    setStreamUrl(videoResponse.data.stream_url);

                    if (videoResponse.data.subtitle_word || videoResponse.data.subtitle_url) {
                        const subtitleUrl =
                            videoResponse.data.subtitle_word || videoResponse.data.subtitle_url;
                        const parsedWords = await fetchSubtitles(subtitleUrl);
                        setWords(parsedWords);
                    }
                }

                await fetchComments();
            } catch (err: any) {
                console.error("Error initializing QuickReview:", err);
                setError(err.response?.data?.detail || "An error occurred loading the video");
            } finally {
                setIsLoading(false);
            }
        };

        init();
    }, [actualVideoId, isSharedAccess, shareToken, navigate, fetchComments]);

    const selectedComment = useMemo(
        () => comments.find(comment => comment.id === selectedCommentId) || null,
        [comments, selectedCommentId]
    );

    const dismissDiscardToast = useCallback(() => {
        if (discardToastIdRef.current) {
            toast.dismiss(discardToastIdRef.current);
            discardToastIdRef.current = null;
        }
    }, []);

    const handleDrawingStart = useCallback(
        (time: number) => {
            dismissDiscardToast();
            setIsPlaying(false);
            setSelectedCommentId(null);
            // A drawing kept for the comment being written is continued on its own frame.
            const drawingTime =
                draftAnnotation?.strokes.length && annotationTime !== null ? annotationTime : time;
            if (drawingTime !== time) {
                playerRef.current?.seekTo(drawingTime, "seconds");
            }
            setAnnotationTime(drawingTime);
            setCurrentTime(drawingTime);
            setIsDrawing(true);
        },
        [draftAnnotation, annotationTime, dismissDiscardToast]
    );

    const discardDrawing = useCallback(() => {
        setDraftAnnotation(null);
        setAnnotationTime(null);
        setIsDrawing(false);
        if (!draftAnnotation?.strokes.length || annotationTime === null) return;

        const discardedAnnotation = draftAnnotation;
        const discardedTime = annotationTime;
        dismissDiscardToast();
        discardToastIdRef.current = toast(
            t => (
                <span className="flex items-center gap-3">
                    Drawing discarded
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs text-primary"
                        onClick={() => {
                            toast.dismiss(t.id);
                            discardToastIdRef.current = null;
                            setSelectedCommentId(null);
                            setIsPlaying(false);
                            playerRef.current?.seekTo(discardedTime, "seconds");
                            setCurrentTime(discardedTime);
                            setAnnotationTime(discardedTime);
                            setDraftAnnotation(discardedAnnotation);
                            setIsDrawing(true);
                        }}
                    >
                        Undo
                    </button>
                </span>
            ),
            { duration: 8000 }
        );
    }, [draftAnnotation, annotationTime, dismissDiscardToast]);

    const handleDrawingSend = useCallback(async () => {
        if (!draftAnnotation?.strokes.length || isSendingDrawing) return;
        setIsSendingDrawing(true);
        try {
            // The comment box posts it, so anything typed there goes with the drawing.
            await composerRef.current?.submit();
        } finally {
            setIsSendingDrawing(false);
        }
    }, [draftAnnotation, isSendingDrawing]);

    const handleAnnotationCommentAdded = useCallback(async () => {
        setDraftAnnotation(null);
        setAnnotationTime(null);
        setIsDrawing(false);
        await fetchComments();
    }, [fetchComments]);

    // A drawing belongs to the paused frame it was made on.
    const isAtDrawingFrame =
        annotationTime !== null &&
        !isPlaying &&
        Math.abs(currentTime - annotationTime) <= DRAWING_FRAME_TOLERANCE;
    const isAwayFromDrawing =
        (isDrawing || Boolean(draftAnnotation?.strokes.length)) && !isAtDrawingFrame;
    const visibleDraftAnnotation = isDrawing || isAtDrawingFrame ? draftAnnotation : null;

    useEffect(() => {
        if (!isAwayFromDrawing || isSendingDrawing) return;
        if (composerRef.current?.hasText()) {
            // Something has been typed, so the drawing stays attached to that comment.
            setIsDrawing(false);
            return;
        }
        discardDrawing();
    }, [isAwayFromDrawing, isSendingDrawing, discardDrawing]);

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.defaultPrevented) return;
            const isInteractiveTarget =
                event.target instanceof Element &&
                event.target.closest(
                    "button, input, textarea, select, a, [contenteditable='true']"
                );
            const isTextTarget =
                event.target instanceof Element &&
                event.target.closest("input, textarea, select, [contenteditable='true']");

            if (isDrawing) {
                if (event.key === "Escape" && !isTextTarget) {
                    event.preventDefault();
                    discardDrawing();
                } else if (event.key === "Enter" && !isInteractiveTarget) {
                    event.preventDefault();
                    handleDrawingSend();
                }
                return;
            }

            if (event.code === "Space" && !isInteractiveTarget) {
                event.preventDefault();
                setIsPlaying(prev => !prev);
            }
        };

        window.addEventListener("keydown", handleKeyPress);
        return () => window.removeEventListener("keydown", handleKeyPress);
    }, [isDrawing, discardDrawing, handleDrawingSend]);

    const visibleCommentAnnotation = useMemo(() => {
        if (isPlaying || !selectedComment?.annotation) return null;
        const rangeEnd = Math.max(selectedComment.start_time, selectedComment.end_time);
        const tolerance = rangeEnd === selectedComment.start_time ? 0.25 : 0;
        const isInRange =
            currentTime >= selectedComment.start_time - tolerance &&
            currentTime <= rangeEnd + tolerance;
        return isInRange ? selectedComment.annotation : null;
    }, [currentTime, isPlaying, selectedComment]);

    const transcriptPanel = useMemo(
        () => (
            <div className="h-full overflow-hidden rounded-xl border border-base-300 bg-base-100 shadow-sm">
                <Transcript
                    words={words}
                    currentTime={currentTime}
                    onWordClick={time => playerRef.current?.seekTo(time, "seconds")}
                />
            </div>
        ),
        [words, currentTime]
    );

    const videoPanel = useMemo(
        () => (
            <div className="h-full overflow-hidden rounded-xl border border-base-300 bg-black shadow-sm">
                <div className="bg-black h-full">
                    <VideoPlayer
                        ref={playerRef}
                        url={streamUrl}
                        playing={isPlaying}
                        onPlayPause={setIsPlaying}
                        onProgress={handleProgress}
                        progressInterval={100}
                        comments={comments}
                        annotation={visibleCommentAnnotation}
                        annotationComment={visibleCommentAnnotation ? selectedComment : null}
                        draftAnnotation={visibleDraftAnnotation}
                        canAnnotate={canComment}
                        isDrawing={isDrawing}
                        isSendingDrawing={isSendingDrawing}
                        onDrawingStart={handleDrawingStart}
                        onDrawingChange={setDraftAnnotation}
                        onDrawingDiscard={discardDrawing}
                        onDrawingSend={handleDrawingSend}
                        onTimeUpdate={setCurrentTime}
                        onCommentClick={handleCommentSelect}
                    />
                </div>
            </div>
        ),
        [
            streamUrl,
            isPlaying,
            handleProgress,
            comments,
            visibleCommentAnnotation,
            selectedComment,
            visibleDraftAnnotation,
            canComment,
            isDrawing,
            isSendingDrawing,
            handleDrawingStart,
            discardDrawing,
            handleDrawingSend,
            handleCommentSelect,
        ]
    );

    const commentsPanel = useMemo(
        () => (
            <div className="h-full overflow-hidden rounded-xl border border-base-300 bg-base-100 shadow-sm">
                <CommentsSection
                    comments={comments}
                    indexId={indexId}
                    onCommentSelect={handleCommentSelect}
                    onSeek={time => {
                        setIsDrawing(false);
                        playerRef.current?.seekTo(time, "seconds");
                        setCurrentTime(time);
                    }}
                    videoId={actualVideoId!}
                    currentTime={currentTime}
                    isPlaying={isPlaying}
                    setIsPlaying={setIsPlaying}
                    onCommentAdded={fetchComments}
                    draftAnnotation={draftAnnotation}
                    annotationTime={annotationTime}
                    onAnnotationCommentAdded={handleAnnotationCommentAdded}
                    onAnnotationClear={discardDrawing}
                    composerRef={composerRef}
                    currentUserEmail={currentUserEmail}
                    isLoading={isLoadingComments}
                    readOnly={!canComment}
                    shareToken={shareToken}
                    selectedCommentId={selectedCommentId}
                    setSelectedCommentId={setSelectedCommentId}
                />
            </div>
        ),
        [
            comments,
            currentTime,
            isPlaying,
            actualVideoId,
            currentUserEmail,
            isLoadingComments,
            canComment,
            indexId,
            fetchComments,
            shareToken,
            selectedCommentId,
            draftAnnotation,
            annotationTime,
            handleAnnotationCommentAdded,
            discardDrawing,
            handleCommentSelect,
        ]
    );

    const getVisiblePanels = () => {
        const panels = [
            {
                key: "transcript",
                element: transcriptPanel,
                paddingClass: showTranscript ? "pr-3" : "px-0",
            },
            {
                key: "video",
                element: videoPanel,
                paddingClass: showComments ? "pr-3" : "px-0",
            },
            {
                key: "comments",
                element: commentsPanel,
                paddingClass: "px-0",
            },
        ];

        return panels.map(panel => (
            <div key={panel.key} className={`h-full ${panel.paddingClass}`}>
                {panel.element}
            </div>
        ));
    };

    const handlePanelResize = (newSizes: number[]) => {
        setPanelSizes(newSizes);
        localStorage.setItem("quick-review-panel-sizes", JSON.stringify(newSizes));
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-96">
                <span className="loading loading-spinner loading-lg text-primary"></span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-96 gap-4">
                <div className="text-xl text-red-500">Error loading video</div>
                <div className="text-gray-600">{error}</div>
                <button onClick={() => navigate("/")} className="btn btn-outline">
                    Return Home
                </button>
            </div>
        );
    }

    if (!video) {
        return null;
    }

    // Share-link guests can't open the owner's collections, so they only see the video name.
    // The last item is the current page and is not rendered as a link.
    const breadcrumbItems = isSharedAccess
        ? [{ label: video.file_name!, path: "" }]
        : [
              { label: "Home", path: APP_HOME },
              {
                  label: video.collection_name!,
                  path: `/quick-review/collections/${video.collection_id}`,
              },
              { label: video.file_name!, path: "" },
          ];

    return (
        <div className="flex h-[calc(100vh-4rem)] flex-col bg-base-200 px-4 pt-2 pb-4 -mt-10">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-3">
                <Breadcrumb items={breadcrumbItems} size="sm" className="py-0 px-0 min-w-0" />
                <div className="flex items-center gap-1.5 shrink-0 ml-auto">
                    <button
                        onClick={handleTranscriptToggle}
                        title={showTranscript ? "Hide transcript panel" : "Show transcript panel"}
                        className={`flex h-8 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                            showTranscript
                                ? "bg-primary/10 text-primary"
                                : "text-gray-500 hover:bg-black/5 hover:text-gray-900"
                        }`}
                    >
                        <LuPanelLeft size={15} />
                        Transcript
                    </button>
                    <button
                        onClick={handleCommentsToggle}
                        title={showComments ? "Hide comments panel" : "Show comments panel"}
                        className={`flex h-8 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                            showComments
                                ? "bg-primary/10 text-primary"
                                : "text-gray-500 hover:bg-black/5 hover:text-gray-900"
                        }`}
                    >
                        <LuPanelRight size={15} />
                        Comments
                    </button>

                    {!isSharedAccess && (
                        <>
                            <div className="mx-1 h-5 w-px bg-base-300" />
                            <button
                                onClick={() => setIsShareModalOpen(true)}
                                className="flex h-8 items-center gap-1.5 rounded-lg bg-primary px-3.5 text-[13px] font-medium text-white shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1"
                            >
                                <LuShare2 size={14} />
                                Share
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="min-h-0 flex-1">
                <ResizablePanels
                    defaultSizes={panelSizes}
                    minSize={0.2}
                    onResize={handlePanelResize}
                    isPlaying={isPlaying}
                    setIsPlaying={setIsPlaying}
                >
                    {getVisiblePanels()}
                </ResizablePanels>
            </div>

            <ShareDialog
                videoId={actualVideoId!}
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
            />
        </div>
    );
};

export default QuickReview;
